import React, { FunctionComponent, MouseEvent, MouseEventHandler, useCallback, useEffect, useState } from 'react';

import { find, isEmpty, includes } from 'lodash';
import { Button, Dropdown, DropdownButton, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { ModuleId } from 'admin/modules/Modules';
// import { AccountType } from 'admin/tenants/Tenants';
import { getUsersByEmail, User, getUser } from 'admin/users/Users';
import {
    authenticate,
    getAuthPage,
    logout,
    Profile,
    ProfileContainer,
    SUPER_AGENCY_ID,
    UNDEFINED_TENANT_ID,
    useCurrentContext,
    useProfile,
    UserType,
} from 'auth/Auth';
import { Avatar, AvatarType } from 'common/avatar/Avatar';
import { NavTab } from 'common/chrome/left-nav/nav-tabs/NavTabs.types';
import { UpsellPicker } from 'common/chrome/left-nav/upsell-picker/UpsellPicker';
import { InternalEmailer } from 'common/chrome/top-nav/internal-emailer/InternalEmailer';
import { LocaleSwitcher } from 'common/chrome/top-nav/locale-switcher/LocaleSwitcher';
import { ImpersonationSwitcher } from 'common/impersonation-switcher/ImpersonationSwitcher';
import { MessageActionType, useMessage } from 'common/message-bar/MessageBar';
import { NotificationTrayButton } from 'common/notification-tray/NotificationTray';
import { Overlay } from 'core/overlay/Overlay';
import { LocaleOptions } from 'core/util/Locale';
import { Route } from 'core/util/Route';
import { route } from 'core/util/Routing';

import 'common/chrome/top-nav/TopNav.scss';

const UpsellButton: FunctionComponent = (): JSX.Element => {
    const { t } = useTranslation(['common/chrome']);
    const [{ profile }] = useProfile();
    const [showUpsellButton, setShowUpsellButton] = useState(true);
    const [showUpsellPicker, setShowUpsellPicker] = useState(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const upsellTab: NavTab = {
        name: 'TOP_UPSELL_NAME',
        buttonName: 'TOP_BUTTON',
        icon: 'icon-lightbulb-on',
        auth: {
            actions: [],
        },
        upsell: {
            title: 'TOP_UPSELL_TITLE',
            description: 'TOP_UPSELL',
            video: 'https://www.youtube.com/embed/CNz80y85Yms',
            modules: [
                ModuleId.MessagingInbox,
                ModuleId.SocialInbox,
                ModuleId.ReviewInbox,
                ModuleId.MessagingCampaigns,
                ModuleId.SurveyInbox,
            ],
        },
        userTypes: [UserType.TenantUser],
    };

    const hasNoModules = useCallback(
        (modules: string[]): boolean => {
            let hasModules = false;

            modules.forEach((module) => {
                if (profile!.modules[module]) {
                    hasModules = true;
                }
            });

            return !hasModules;
        },
        [profile],
    );

    useEffect(() => {
        setShowUpsellButton(false);

        /* Tenant has no modules
        if (hasNoModules(upsellTab.upsell!.modules)) {
            // Disabled tenants
            if (profile!.tenant && [43401, 127718, 9866].includes(profile!.tenant.id)) {
                setShowUpsellButton(false);
            }

            // User has declined
            if (profile!.user.preferences && profile!.user.preferences[`upsell-${upsellTab.name}`] === 'false') {
                setShowUpsellButton(false);
            }

            // Filter Competitor2 tenants for Competitive Intelligence
            if (profile!.tenant?.accountType === AccountType.Competitor2 || profile!.tenant?.id === 11743) {
                setShowUpsellButton(false);
            }
        } else {
            setShowUpsellButton(false);
        } */
    }, [upsellTab, profile, hasNoModules, setShowUpsellButton]);

    if (!showUpsellButton) {
        return <></>;
    }

    return (
        <>
            <Button
                variant="primary"
                className="upsell-button"
                onClick={(e: MouseEvent): void => {
                    setShowUpsellPicker(true);
                }}
            >
                <i className={upsellTab.icon} /> <span>{t(upsellTab.buttonName!)}</span>
            </Button>
            {/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <span onClick={(e: MouseEvent): void => e.stopPropagation()}>
                <UpsellPicker
                    tab={upsellTab}
                    show={showUpsellPicker}
                    onClose={(e: MouseEvent): void => {
                        setShowUpsellPicker(false);
                    }}
                />
            </span>
            {/* eslint-enable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        </>
    );
};

const getUnimpersonateUrl = (profile: Profile): string => {
    let url = `${Route.Impersonation}?mode=unimpersonate`;

    if (profile.user.agencyID !== profile.impersonatedBy?.agencyId) {
        url += `&aid=${profile.impersonatedBy?.agencyId}&tid=${UNDEFINED_TENANT_ID}&uid=${profile.impersonatedBy?.userId}`;
    } else {
        url += `&aid=${profile.user.agencyID}&tid=${profile.user.tenantID}&uid=${profile.user.id}`;
    }

    return route(url);
};

const Logo: FunctionComponent<{ profile: Profile }> = ({ profile }): JSX.Element => {
    const [tenantLogo, setTenantLogo] = useState(profile?.tenant?.smallLogoPath);
    const [agencyLogo, setAgencyLogo] = useState(profile?.agency?.logo);
    const [tenantName, setTenantName] = useState(profile.tenant?.name);
    const [agencyName, setAgencyName] = useState(profile?.agency?.name);

    const { agencyId, tenantId, getAgency, getTenant } = useCurrentContext();

    useEffect(() => {
        async function fetchTenantData(): Promise<void> {
            const data = await getTenant();

            setTenantLogo(data?.smallLogoPath);
            setTenantName(data?.name);
        }

        async function fetchAgencyData(): Promise<void> {
            const data = await getAgency();

            setAgencyLogo(data?.logo);
            setAgencyName(data?.name);
        }

        if (agencyId && tenantId) {
            fetchTenantData();
        } else if (agencyId) {
            fetchAgencyData();
        }
    }, [agencyId, getAgency, getTenant, tenantId]);

    if (tenantLogo) {
        return (
            <img src={tenantLogo} alt={tenantName} className="top-nav-logo" onError={(): void => setTenantLogo('')} />
        );
    }

    if (tenantName) {
        return <div className="top-nav-name">{tenantName}</div>;
    }

    if (agencyLogo) {
        return (
            <img src={agencyLogo} alt={agencyName} className="top-nav-logo" onError={(): void => setAgencyLogo('')} />
        );
    }

    if (agencyName) {
        return <div className="top-nav-name">{agencyName}</div>;
    }

    return <></>;
};

interface Props {
    onNavToggle: MouseEventHandler;
}

// const ResourcesDropdown: FunctionComponent = (): JSX.Element | null => {
//     const { t } = useTranslation(['common/chrome']);
//     const [{ profile }] = useProfile();

//     if (!profile || profile.isAgencyUser) {
//         return null;
//     }

//     // Build subtabs list with same logic as NavTabs
//     const subtabsList: any[] = [
//         {
//             name: 'REPUTATION_NEWS',
//             path: 'https://reputation.com/reputation-news',
//             icon: 'icon-address-card',
//             newTab: true,
//         },
//         {
//             name: 'IDEA_PORTAL',
//             path: `${Route.IdeaPortal}`,
//             icon: 'icon-lightbulb',
//             newTab: true,
//         },
//         {
//             name: 'SUPPORT_PORTAL',
//             path: 'https://support.reputation.com',
//             icon: 'icon-question-circle',
//             newTab: true,
//         },
//     ];

//     if (!profile.isImpersonated) {
//         subtabsList.push({
//             name: 'PLATFORM_EDUCATION',
//             path: '',
//             icon: 'icon-award',
//             newTab: true,
//             onClick: () => {
//                 (async (): Promise<any> => {
//                     try {
//                         const sourceResult = await trackPromise(
//                             getSkillJarLink(profile?.tenant?.agencyID!, Number(profile?.tenant?.id), profile?.user.id!),
//                         );

//                         if (sourceResult?.url) {
//                             window.open(sourceResult.url, '_blank');
//                         }
//                     } catch {}
//                 })();
//             },
//         });
//     }

//     // Apply filtering logic
//     let filteredSubtabs = subtabsList;

//     // For EU/RU, remove Reputation News if not US/GB locale
//     if (
//         (window.location.href.includes('app-eu') || window.location.href.includes('app-ru')) &&
//         ![LocaleCode.en_US, LocaleCode.en_GB].includes(profile.user.locale)
//     ) {
//         filteredSubtabs = filteredSubtabs.filter((subTab) => subTab.name !== 'REPUTATION_NEWS');
//     }

//     // Remove Idea Portal for impersonated users & RU customers
//     if (profile.isImpersonated || window.location.href.includes('app-ru')) {
//         filteredSubtabs = filteredSubtabs.filter((subTab) => subTab.name !== 'IDEA_PORTAL');
//     }

//     // Remove Reputation News for GM, Ford and Nissan
//     if (profile.tenant && [11743, 289, 43401].includes(profile.tenant.id)) {
//         filteredSubtabs = filteredSubtabs.filter((subTab) => subTab.name !== 'REPUTATION_NEWS');
//     }

//     // Disable for Rent.com
//     if (profile.tenant && [176094].includes(profile.tenant.id)) {
//         return null;
//     }

//     const tooltipOffset = { modifiers: [{ name: 'offset', options: { offset: [0, 15] } }] };

//     return (
//         <Dropdown className="top-nav-resources" alignRight>
//             <OverlayTrigger
//                 placement="bottom"
//                 popperConfig={tooltipOffset}
//                 overlay={<Tooltip id="resources-tooltip">{t('RESOURCES')}</Tooltip>}
//             >
//                 <Dropdown.Toggle as={Button} variant="icon" id="resources">
//                     <i className="icon-question-circle" />
//                 </Dropdown.Toggle>
//             </OverlayTrigger>

//             <Dropdown.Menu>
//                 {filteredSubtabs.map((subTab) => {
//                     if (subTab.onClick) {
//                         return (
//                             <Dropdown.Item
//                                 key={subTab.name}
//                                 href=""
//                                 onClick={(e: MouseEvent): void => {
//                                     e.preventDefault();
//                                     subTab.onClick();
//                                 }}
//                             >
//                                 <i className={subTab.icon} />
//                                 &nbsp;<span>{t(subTab.name)}</span>
//                             </Dropdown.Item>
//                         );
//                     }

//                     return (
//                         <Dropdown.Item
//                             key={subTab.name}
//                             href={subTab.path}
//                             target={subTab.newTab ? '_blank' : undefined}
//                             rel={subTab.newTab ? 'noopener noreferrer' : undefined}
//                         >
//                             <i className={subTab.icon} />
//                             &nbsp;<span>{t(subTab.name)}</span>
//                         </Dropdown.Item>
//                     );
//                 })}
//             </Dropdown.Menu>
//         </Dropdown>
//     );
// };

export const TopNav: FunctionComponent<Props> = ({ onNavToggle }) => {
    const [{ profile }] = useProfile() as [ProfileContainer, any];
    const [, messageDispatch] = useMessage();
    const { t } = useTranslation(['common/chrome']);
    const [showImpersonationSwitcher, setShowImpersonationSwitcher] = useState(false);
    const [showInternalEmailer, setShowInternalEmailer] = useState(false);
    const [showLocaleSwitcher, setShowLocaleSwitcher] = useState(false);
    const [users, setUsers] = useState<User[]>([]);

    if (!profile) {
        return <div className="r4e-top-nav" />;
    }

    const tooltipOffset = { modifiers: [{ name: 'offset', options: { offset: [0, 15] } }] };
    const userDropdownMenuOffset = { modifiers: [{ name: 'offset', options: { offset: [0, 20] } }] };

    const impersonationSwitcherOptions = {
        agencyId: profile.user.agencyID,
        impersonatedByAgencyId: profile.impersonatedBy?.agencyId,
    };

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
        (async (): Promise<any> => {
            if (profile.hasMultipleAccounts && isEmpty(users)) {
                try {
                    let impersonatedByUser: any;
                    const usersList = await trackPromise(
                        getUsersByEmail(profile!.user.agencyID, profile!.user.tenantID, profile!.user.email, {
                            includeTenants: true,
                            basic: false,
                        }),
                        'users',
                    );

                    if (profile.impersonatedBy) {
                        impersonatedByUser = await trackPromise(
                            getUser(profile!.user.agencyID, profile!.user.tenantID, profile.impersonatedBy.userId),
                            'users',
                        );
                    }

                    setUsers(
                        usersList.filter((user: any) => {
                            return (
                                user.id !== profile!.user.id &&
                                user.tenant?.active &&
                                (profile.impersonatedBy
                                    ? includes(impersonatedByUser.accountTypes, user.tenant.accountType.toString())
                                    : true)
                            );
                        }),
                    );
                } catch {}
            }
        })();
    }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

    const switchImpersonation = (e: MouseEvent | null, user?: User): void => {
        setShowImpersonationSwitcher(false);

        if (user) {
            window.location.href = route(
                `${Route.Impersonation}?mode=switch&aid=${user.agencyID}&tid=${user.tenantID}&uid=${user.id}`,
            );
        }
    };

    const switchUser = async (e: any, user: User): Promise<void> => {
        if (e) {
            e.stopPropagation(); // Prevent dropdown from closing
        }

        try {
            await trackPromise(authenticate(user, true), 'users');
            window.location.href = route(`${Route.Auth}`); // Angular will redirect to landing page
        } catch {
            messageDispatch({
                type: MessageActionType.ShowError,
                text: t('FAILED_TO_SWITCH_USERS'),
            });
        }
    };

    const userLocale = find(LocaleOptions, (l) => l.value === profile.user.locale);

    const onLogout = async (e: MouseEvent): Promise<void> => {
        try {
            await logout();
            window.location.href = getAuthPage();
        } catch {}
    };

    return (
        <div role="navigation" aria-label={t('TOP_NAVIGATION')} className="r4e-top-nav">
            <button
                type="button"
                className="btn btn-icon top-nav-left-nav-trigger"
                onClick={onNavToggle}
                title={t('TOGGLE_NAVIGATION')}
                aria-label={t('TOGGLE_NAVIGATION')}
            >
                <i className="icon-reputation" />
            </button>

            {/* Logo */}
            <Logo profile={profile} />

            <div className="top-nav-admin">
                {/* Reputation Logo */}
                {profile.user.agencyID === SUPER_AGENCY_ID && (
                    <div className="reputation-logo">
                        <img src="/rsrc2/images/uservoice/reputation-logo.png" alt="Reputation" />
                    </div>
                )}

                {/* Filters */}
                {/* <OverlayTrigger placement="auto" overlay={<Tooltip id="tooltip-filters">{t('FILTERS')}</Tooltip>}>
                    <Button variant="icon" as="a" className="top-nav-filter-trigger" href="#TODO">
                        <i className="icon-filter" />
                    </Button>
                </OverlayTrigger> */}

                {/* Upsell */}
                <UpsellButton />

                {/* Impersonation */}
                {profile.isImpersonated || (profile.hasMultipleAccounts && users.length) ? (
                    <DropdownButton
                        className="top-nav-impersonation"
                        id="switcher"
                        title={
                            <>
                                <i className="icon-user" />{' '}
                                <span className="top-nav-impersonation-label">{t('SWITCH')}</span>
                            </>
                        }
                        variant="default"
                        size="sm"
                        alignRight
                    >
                        {(profile.isImpersonated && profile.user.agencyID === profile.impersonatedBy?.agencyId) ||
                        profile.impersonatedBy?.agencyId === SUPER_AGENCY_ID ? (
                            <Dropdown.Item href="" onClick={(): void => setShowImpersonationSwitcher(true)}>
                                {t('SWITCH_TENANT')}
                            </Dropdown.Item>
                        ) : null}
                        {profile.isImpersonated ? (
                            <Dropdown.Item href={getUnimpersonateUrl(profile)}>{t('BACK_TO_ADMIN')}</Dropdown.Item>
                        ) : null}
                        {profile.isImpersonated && profile.hasMultipleAccounts && users.length ? (
                            <Dropdown.Divider />
                        ) : null}
                        {profile.hasMultipleAccounts ? (
                            <>
                                <Overlay area="users" />
                                {users.map((user) => {
                                    return (
                                        <Dropdown.Item href="" onClick={(e): Promise<void> => switchUser(e, user)}>
                                            {user.tenant?.name}
                                        </Dropdown.Item>
                                    );
                                })}
                            </>
                        ) : null}
                    </DropdownButton>
                ) : (
                    ''
                )}

                {/* Notification Tray */}
                {!profile.isAgencyUser ? <NotificationTrayButton /> : ''}

                {/* Settings */}
                {!profile.isAgencyUser ? (
                    <Button
                        variant="icon"
                        as="a"
                        aria-label={t('SETTINGS')}
                        className="top-nav-settings"
                        href={`${process.env.REACT_APP_OLD_STACK_HOST}/admin/agencies/${profile.user.agencyID}/tenants/${profile.user.tenantID}/my-account`}
                    >
                        <OverlayTrigger
                            placement="bottom"
                            popperConfig={tooltipOffset}
                            overlay={<Tooltip id="settings-tooltip">{t('SETTINGS')}</Tooltip>}
                        >
                            <i className="icon-cog" />
                        </OverlayTrigger>
                    </Button>
                ) : (
                    ''
                )}

                {/* User */}
                <Dropdown className="top-nav-user">
                    <OverlayTrigger
                        placement="bottom"
                        popperConfig={tooltipOffset}
                        overlay={<Tooltip id="avatar-tooltip">{profile.user.name}</Tooltip>}
                    >
                        <Dropdown.Toggle id="user" as="a">
                            <Avatar type={AvatarType.User} object={profile.user} />
                        </Dropdown.Toggle>
                    </OverlayTrigger>
                    <Dropdown.Menu popperConfig={userDropdownMenuOffset}>
                        <Dropdown.Item href="" onClick={(): void => setShowLocaleSwitcher(true)}>
                            <i className="icon-language" />
                            &nbsp;
                            <span>{t('LOCALE', { locale: userLocale?.label })}</span>
                        </Dropdown.Item>
                        {/* Removing Internal Emailer option for all users as per CORE-2990 */}
                        {/* {!profile.isAgencyUser ? (
                            <Dropdown.Item href="" onClick={(): void => setShowInternalEmailer(true)}>
                                <i className="icon-envelope" />
                                &nbsp;<span>{t('INTERNAL_EMAILER')}</span>
                            </Dropdown.Item>
                        ) : (
                            ''
                        )} */}
                        <Dropdown.Divider />
                        <Dropdown.Item href="" onClick={onLogout}>
                            <i className="icon-sign-out" />
                            &nbsp;<span>{t('LOGOUT')}</span>
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>

                {/* Impersonation Switcher */}
                <ImpersonationSwitcher
                    show={showImpersonationSwitcher}
                    onClose={switchImpersonation}
                    impersonationSwitcherOptions={impersonationSwitcherOptions}
                />

                {/* Locale Switcher */}
                <LocaleSwitcher show={showLocaleSwitcher} onClose={(): void => setShowLocaleSwitcher(false)} />

                {/* Internal Emailer */}
                <InternalEmailer show={showInternalEmailer} onClose={(): void => setShowInternalEmailer(false)} />
            </div>
        </div>
    );
};
